import isNil from "lodash/isNil";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import ChangeIndication from "app/components/ChangeIndication/ChangeIndication";
import { TitleWithInfo } from "app/components/TitleWithInfo/TitleWithInfo";
import { translate } from "app/filters/i18nFilter";
import { KEYWORDS, ITEM_LOADER_HEIGHT, ITEM_LOADER_WIDTH } from "app/pages/activation/components/RankWidget/config";
import { widgetItemKeys } from "app/pages/activation/components/RankWidget/dataAutomationKeys";
import { getRTDates } from "app/pages/activation/components/RankWidget/helpers/getRTDates";
import { openRankTrackerPage } from "app/pages/activation/components/RankWidget/helpers/openRankTrackerPage";
import { ZERO } from "app/pages/activation/components/Topics/Topic/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import type { RankTrackerCampaign } from "app/pages/activation/types/api";
import { trendReportPresetsSelectionConfig } from "app/pages/digital-marketing/keywords-campaigns/reports/trend-report/TrendReport.consts";

import * as SC from "./styles";

type WidgetItemSchema = {
    name: string;
    title: string;
    tooltip: string;
    change?: number;
    isInverted?: boolean;
    isLoading?: boolean;
    isScraping?: boolean;
    RTCampaign?: RankTrackerCampaign;
};

export const WidgetItem = ({
    title,
    tooltip,
    change,
    name,
    isInverted = false,
    isLoading = false,
    isScraping = false,
    RTCampaign,
}: WidgetItemSchema) => {
    const changeValue = isNil(change) ? "" : Math.abs(change);
    const isIncreasing = isInverted ? change === ZERO : change > ZERO;
    const isActive = !isScraping && !isLoading;
    const { rankTracker } = activationTracking;

    const onItemClick = () => {
        if (!isActive || !RTCampaign) {
            return;
        }

        rankTracker.trackClickOnFilter(name, change);

        const filters = {} as Record<string, string>;

        if (change !== ZERO) {
            const { previousPosition, position } = trendReportPresetsSelectionConfig.find(({ id }) => id === name);

            filters.useCaseFilter = name;
            filters.previousPosition = previousPosition;
            filters.positions = position;
        }

        const { duration } = getRTDates(RTCampaign);

        openRankTrackerPage({
            campaignId: RTCampaign.id,
            scrapingConfigurations: RTCampaign.scrapingConfigurations,
            site: RTCampaign.site,
            duration,
            filters,
        });
    };

    if (isLoading) {
        return (
            <SC.Container
                isLoading={isLoading}
                isActive={false}
                data-automation={widgetItemKeys.container(name)}
                data-loading="true"
            >
                <PixelPlaceholderLoader
                    height={ITEM_LOADER_HEIGHT}
                    width={ITEM_LOADER_WIDTH}
                    data-automation="1234"
                />
            </SC.Container>
        );
    }

    return (
        <SC.Container
            data-automation={widgetItemKeys.container(name)}
            isActive={isActive}
            onClick={onItemClick}
        >
            <SC.Header data-automation={widgetItemKeys.header}>
                <TitleWithInfo
                    title={translate(title)}
                    tooltip={translate(tooltip)}
                    size="medium"
                    fontVariant="subtitle1"
                    dataAutomation={widgetItemKeys.title(name)}
                />

                <SWReactIcons
                    iconName="arrow-slant-right"
                    size="xs"
                    iconColor={isScraping ? colorsPalettes.carbon[100] : colorsPalettes.carbon[300]}
                />
            </SC.Header>

            <ChangeIndication
                className="keywordChangeIndication"
                changeValue={`${changeValue} ${KEYWORDS}`}
                isIncreasing={isIncreasing}
                isNotChanged={isNil(change)}
                withoutArrows={+changeValue === ZERO}
                dataAutomation={widgetItemKeys.change(name)}
            />
        </SC.Container>
    );
};
